import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Sparkles, X, Check, Square, CheckSquare, Zap } from 'lucide-react';
import { Input } from '@/components/ui/input';

export default function ProductSeoPanel() {
  const qc = useQueryClient();
  const { data: products = [] } = useQuery({
    queryKey: ['products'],
    queryFn: () => base44.entities.Product.list('-created_date'),
  });
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ seo_title: '', seo_description: '', keywords: [] });
  const [saving, setSaving] = useState(false);

  const [selected, setSelected] = useState(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkResult, setBulkResult] = useState(null);

  const list = products.filter((p) => p.published !== false);

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelected((prev) => (prev.size === list.length ? new Set() : new Set(list.map((p) => p.id))));
  };

  const generate = async (p) => {
    setEditing(p);
    setLoading(true);
    setForm({ seo_title: p.seo_title || '', seo_description: p.seo_description || '', keywords: [] });
    try {
      const res = await base44.functions.invoke('generateProductSeo', { productId: p.id });
      const s = res.data?.suggestion;
      if (s) setForm({ seo_title: s.seo_title || '', seo_description: s.seo_description || '', keywords: s.keywords || [] });
    } catch (_e) {}
    setLoading(false);
  };

  const apply = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await base44.entities.Product.update(editing.id, { seo_title: form.seo_title, seo_description: form.seo_description });
      qc.invalidateQueries({ queryKey: ['products'] });
      setEditing(null);
    } catch (_e) {}
    setSaving(false);
  };

  const bulkGenerate = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBulkRunning(true);
    setBulkResult(null);
    setBulkProgress(0);
    let done = 0;
    let errors = 0;
    for (const id of ids) {
      try {
        const res = await base44.functions.invoke('generateProductSeo', { productId: id });
        const s = res.data?.suggestion;
        if (s?.seo_title || s?.seo_description) {
          await base44.entities.Product.update(id, { seo_title: s.seo_title || '', seo_description: s.seo_description || '' });
          done++;
        } else {
          errors++;
        }
      } catch (_e) {
        errors++;
      }
      setBulkProgress(Math.round(((done + errors) / ids.length) * 100));
    }
    setBulkResult({ done, errors, total: ids.length });
    setBulkRunning(false);
    setSelected(new Set());
    qc.invalidateQueries({ queryKey: ['products'] });
  };

  const allSelected = list.length > 0 && selected.size === list.length;

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="text-white/40 text-[10px] font-bold tracking-widest mb-1">PRODUCT SEO</p>
          <p className="text-white/60 text-sm">Generate AI-optimised SEO titles &amp; descriptions. Review individually, or bulk-generate &amp; apply for selected products.</p>
        </div>
        {selected.size > 0 && (
          <button
            onClick={bulkGenerate}
            disabled={bulkRunning}
            className="bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest px-5 py-2.5 disabled:opacity-40 flex items-center gap-2"
          >
            {bulkRunning ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {bulkProgress}%</>
            ) : (
              <><Zap className="w-4 h-4" /> GENERATE FOR {selected.size} SELECTED</>
            )}
          </button>
        )}
      </div>

      {bulkResult && (
        <div className={`mb-4 px-4 py-3 text-xs border ${bulkResult.errors ? 'border-yellow-500/30 bg-yellow-500/10 text-yellow-300' : 'border-green-500/30 bg-green-500/10 text-green-300'}`}>
          Bulk generate complete — {bulkResult.done} applied{bulkResult.errors ? ` · ${bulkResult.errors} error(s)` : ''}.
        </div>
      )}

      <div className="border border-white/5 overflow-x-auto">
        <div className="grid grid-cols-12 px-4 py-2.5 bg-white/[0.03] text-white/30 text-[10px] font-bold tracking-widest items-center min-w-[640px]">
          <div className="col-span-1">
            <button onClick={toggleSelectAll} className="text-white/30 hover:text-white transition-colors">
              {allSelected ? <CheckSquare className="w-4 h-4 text-primary" /> : <Square className="w-4 h-4" />}
            </button>
          </div>
          <div className="col-span-4">PRODUCT</div>
          <div className="col-span-4">SEO TITLE</div>
          <div className="col-span-2">DESCRIPTION</div>
          <div className="col-span-1 text-right"></div>
        </div>
        {list.length === 0 && <div className="px-4 py-6 text-white/30 text-sm">No products.</div>}
        {list.map((p) => {
          const isSel = selected.has(p.id);
          return (
            <div key={p.id} className={`grid grid-cols-12 px-4 py-3 border-t border-white/5 items-center text-sm min-w-[640px] ${isSel ? 'bg-primary/5' : ''}`}>
              <div className="col-span-1">
                <button onClick={() => toggleSelect(p.id)} className="text-white/30 hover:text-white transition-colors">
                  {isSel ? <CheckSquare className="w-4 h-4 text-primary" /> : <Square className="w-4 h-4" />}
                </button>
              </div>
              <div className="col-span-4 text-white truncate">{p.name}</div>
              <div className="col-span-4 text-white/50 truncate text-xs">{p.seo_title || <span className="text-white/20 italic">not set</span>}</div>
              <div className="col-span-2 text-xs">
                {p.seo_description ? <Check className="w-3.5 h-3.5 text-green-400" /> : <span className="text-white/20">—</span>}
              </div>
              <div className="col-span-1 text-right">
                <button onClick={() => generate(p)} className="text-primary hover:text-primary/80 text-[10px] font-bold tracking-widest">GENERATE</button>
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#111115] border border-white/10 w-full max-w-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-white font-bold text-sm truncate pr-4">{editing.name}</h3>
              <button onClick={() => setEditing(null)} className="text-white/40 hover:text-white shrink-0"><X className="w-4 h-4" /></button>
            </div>
            {loading ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">SEO TITLE</label>
                  <Input value={form.seo_title} onChange={(e) => setForm({ ...form, seo_title: e.target.value })} className="bg-white/5 border-white/10 text-white" />
                </div>
                <div>
                  <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">META DESCRIPTION</label>
                  <textarea value={form.seo_description} onChange={(e) => setForm({ ...form, seo_description: e.target.value })} rows={3} className="w-full bg-white/5 border border-white/10 text-white text-sm px-3 py-2 outline-none resize-none" />
                </div>
                {form.keywords.length > 0 && (
                  <div>
                    <label className="text-white/40 text-[10px] font-bold tracking-widest mb-1.5 block">SUGGESTED KEYWORD TAGS</label>
                    <div className="flex flex-wrap gap-1.5">
                      {form.keywords.map((k) => (
                        <span key={k} className="text-primary text-[10px] bg-primary/10 border border-primary/20 px-2 py-1">{k}</span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex gap-2 pt-2">
                  <button onClick={() => generate(editing)} className="bg-primary/10 border border-primary/40 hover:bg-primary hover:border-primary text-white font-bold text-xs tracking-widest px-4 py-2.5 flex items-center gap-2 transition-colors">
                    <Sparkles className="w-3.5 h-3.5" /> REGENERATE
                  </button>
                  <button onClick={apply} disabled={saving} className="flex-1 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-widest py-2.5 disabled:opacity-40 flex items-center justify-center gap-2">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} APPLY TO PRODUCT
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}